import type {
  ConnectionStatus,
  WebSocketMessage,
  WebSocketError,
  MessageCallback,
  StatusChangeCallback,
  ErrorCallback,
  ChannelOptions
} from './types'

/**
 * WebSocket connection manager with automatic reconnection and error handling
 */
export class WebSocketConnection {
  private ws: WebSocket | null = null
  private url: string
  private options: {
    autoReconnect: boolean
    maxReconnectAttempts: number
    reconnectDelay: number
    authToken: string | (() => string)
  }
  private reconnectAttempts: number = 0
  private reconnectTimer: number | null = null
  private status: ConnectionStatus = 'disconnected'

  // Callback arrays
  private messageCallbacks: MessageCallback[] = []
  private statusCallbacks: StatusChangeCallback[] = []
  private errorCallbacks: ErrorCallback[] = []

  // Connection state
  private isManuallyDisconnected: boolean = false

  constructor(url: string, options: ChannelOptions = {}) {
    this.url = url
    this.options = {
      autoReconnect: options.autoReconnect ?? true,
      maxReconnectAttempts: options.maxReconnectAttempts ?? 5,
      reconnectDelay: options.reconnectDelay ?? 1000,
      authToken: options.authToken ?? ''
    }
  }

  /**
   * Establish WebSocket connection
   */
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.CONNECTING || this.ws?.readyState === WebSocket.OPEN) {
      return
    }

    this.isManuallyDisconnected = false
    this.setStatus('connecting')

    try {
      // Build WebSocket URL with auth token if provided
      const wsUrl = this.buildWebSocketUrl()
      this.ws = new WebSocket(wsUrl)

      this.setupEventListeners()

      // Return promise that resolves when connection is established
      return new Promise((resolve, reject) => {
        const onOpen = () => {
          this.ws?.removeEventListener('open', onOpen)
          this.ws?.removeEventListener('error', onError)
          resolve()
        }

        const onError = (_event: Event) => {
          this.ws?.removeEventListener('open', onOpen)
          this.ws?.removeEventListener('error', onError)
          reject(new Error('Failed to establish WebSocket connection'))
        }

        this.ws?.addEventListener('open', onOpen)
        this.ws?.addEventListener('error', onError)
      })
    } catch (error) {
      this.handleConnectionError(error)
      throw error
    }
  }

  /**
   * Close WebSocket connection
   */
  disconnect(): void {
    this.isManuallyDisconnected = true
    this.clearReconnectTimer()

    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect')
      this.ws = null
    }

    this.setStatus('disconnected')
  }

  /**
   * Send data through WebSocket
   */
  send(data: any): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data))
    } else {
      this.handleError({
        type: 'network_error',
        message: 'Cannot send message: WebSocket is not connected'
      })
    }
  }

  /**
   * Add message callback
   */
  onMessage(callback: MessageCallback): () => void {
    this.messageCallbacks.push(callback)

    // Return unsubscribe function
    return () => {
      const index = this.messageCallbacks.indexOf(callback)
      if (index > -1) {
        this.messageCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Add status change callback
   */
  onStatusChange(callback: StatusChangeCallback): () => void {
    this.statusCallbacks.push(callback)

    // Return unsubscribe function
    return () => {
      const index = this.statusCallbacks.indexOf(callback)
      if (index > -1) {
        this.statusCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Add error callback
   */
  onError(callback: ErrorCallback): () => void {
    this.errorCallbacks.push(callback)

    // Return unsubscribe function
    return () => {
      const index = this.errorCallbacks.indexOf(callback)
      if (index > -1) {
        this.errorCallbacks.splice(index, 1)
      }
    }
  }

  /**
   * Get current connection status
   */
  getStatus(): ConnectionStatus {
    return this.status
  }

  /**
   * Get current reconnect attempt count
   */
  getReconnectAttempts(): number {
    return this.reconnectAttempts
  }

  /**
   * Check if connection is active
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  /**
   * Resolve the auth token, calling the getter if it is a function.
   *
   * :return: The current authentication token string
   */
  private resolveAuthToken(): string {
    const token = this.options.authToken
    return typeof token === 'function' ? token() : token
  }

  /**
   * Build WebSocket URL with authentication
   */
  private buildWebSocketUrl(): string {
    const url = new URL(this.url, window.location.origin)

    // Convert http/https to ws/wss
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'

    // Resolve token fresh on every connection/reconnection attempt
    const authToken = this.resolveAuthToken()
    if (authToken) {
      url.searchParams.set('token', authToken)
    }

    return url.toString()
  }

  /**
   * Setup WebSocket event listeners
   */
  private setupEventListeners(): void {
    if (!this.ws) return

    this.ws.addEventListener('open', this.handleOpen.bind(this))
    this.ws.addEventListener('message', this.handleMessage.bind(this))
    this.ws.addEventListener('close', this.handleClose.bind(this))
    this.ws.addEventListener('error', this.handleWebSocketError.bind(this))
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(): void {
    this.reconnectAttempts = 0
    this.setStatus('connected')
  }

  /**
   * Handle WebSocket message event
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data)
      this.messageCallbacks.forEach((callback) => {
        try {
          callback(message)
        } catch (error) {
          console.error('Error in message callback:', error)
        }
      })
    } catch (error) {
      this.handleError({
        type: 'unknown',
        message: 'Failed to parse WebSocket message'
      })
    }
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    this.ws = null

    if (this.isManuallyDisconnected) {
      this.setStatus('disconnected')
      return
    }

    // Handle different close codes
    if (event.code === 1006) {
      // Abnormal closure - likely network issue
      this.handleConnectionError(new Error('Connection lost'))
    } else if (event.code === 1008) {
      // Policy violation - likely auth issue
      this.handleError({
        type: 'auth_failed',
        message: 'Authentication failed',
        code: event.code
      })
    } else if (event.code === 1011) {
      // Server error
      this.handleError({
        type: 'unknown',
        message: 'Server error',
        code: event.code
      })
    } else {
      // Other close reasons
      this.handleConnectionError(
        new Error(`Connection closed: ${event.reason || 'Unknown reason'}`)
      )
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleWebSocketError(_event: Event): void {
    this.handleConnectionError(new Error('WebSocket error occurred'))
  }

  /**
   * Handle connection errors and trigger reconnection
   */
  private handleConnectionError(error: any): void {
    this.setStatus('error')

    this.handleError({
      type: 'network_error',
      message: error.message || 'Connection error'
    })

    if (this.options.autoReconnect && !this.isManuallyDisconnected) {
      this.scheduleReconnect()
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
      this.setStatus('error')
      this.handleError({
        type: 'network_error',
        message: `Max reconnection attempts (${this.options.maxReconnectAttempts}) exceeded`
      })
      return
    }

    this.setStatus('reconnecting')
    this.reconnectAttempts++

    // Exponential backoff: delay * (2 ^ attempts) with jitter
    const baseDelay = this.options.reconnectDelay
    const exponentialDelay = baseDelay * Math.pow(2, this.reconnectAttempts - 1)
    const jitter = Math.random() * 1000 // Add up to 1 second of jitter
    const delay = Math.min(exponentialDelay + jitter, 30000) // Cap at 30 seconds

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.connect().catch((error) => {
        console.error('Reconnection failed:', error)
      })
    }, delay)
  }

  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  /**
   * Set connection status and notify callbacks
   */
  private setStatus(status: ConnectionStatus): void {
    if (this.status !== status) {
      this.status = status
      this.statusCallbacks.forEach((callback) => {
        try {
          callback(status)
        } catch (error) {
          console.error('Error in status callback:', error)
        }
      })
    }
  }

  /**
   * Handle and propagate errors
   */
  private handleError(error: WebSocketError): void {
    this.errorCallbacks.forEach((callback) => {
      try {
        callback(error)
      } catch (callbackError) {
        console.error('Error in error callback:', callbackError)
      }
    })
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    this.disconnect()
    this.messageCallbacks.length = 0
    this.statusCallbacks.length = 0
    this.errorCallbacks.length = 0
  }
}
